
import {Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges} from '@angular/core';
import { FormControl } from '@angular/forms';
import { ClinicSearchDropdownComponent } from '@app/shared/components/clinic-search-dropdown/clinic-search-dropdown.component';
import { ProviderMultiselectComponent } from '@app/shared/components/provider-multiselect/provider-multiselect.component';
import { RemoveButtonComponent } from '@app/shared/components/remove-button/remove-button.component';
import {
  ClinicProviderInfoSectionProps
} from "@app/features/adminDashboard/interfaces/clinicProviderInfoSectionProps.interface";
import {ReferralUser} from "@app/core/interfaces/user.interface";
import {Clinic} from '@app/shared/interfaces/clinic.interface';

@Component({
  selector: 'app-clinic-provider-info-section',
  imports: [
    ClinicSearchDropdownComponent,
    ProviderMultiselectComponent,
    RemoveButtonComponent
],
  templateUrl: './clinic-provider-info-section.component.html',
  styleUrl: './clinic-provider-info-section.component.scss'
})
export class ClinicProviderInfoSectionComponent implements OnChanges {
  @Input() mode: 'readOnly' | 'edit' = 'readOnly';
  @Input() allowSecondProviderRemoval: boolean = false;
  @Input() firstClinicProps?: ClinicProviderInfoSectionProps | null;
  @Input() secondClinicProps?: ClinicProviderInfoSectionProps | null;
  @Input() showPendingSecondClinicRemoval = true;
  @Input() clinics: Clinic[] = [];
  @Input() firstClinicCtrl!: FormControl;
  @Input() firstProvidersCtrl!: FormControl;
  @Input() secondClinicCtrl!: FormControl;
  @Input() secondProvidersCtrl!: FormControl;
  @Output() firstClinicOtherProviderRemoved = new EventEmitter();
  @Output() secondClinicOtherProviderRemoved = new EventEmitter();
  @Output() firstClinicRemoveRequestRemoved = new EventEmitter();
  @Output() secondClinicRemoveRequestRemoved = new EventEmitter();


  firstClinicIdValue: string | null = null;
  secondClinicIdValue: string | null = null;
  hasFirstClinicId = false;
  hasSecondClinicId = false;
  removeFirstClinicOtherProviderPanel = false;
  removeSecondClinicOtherProviderPanel = false;

  removeFirstRemoveProviderPanel = false;
  removeSecondRemoveProviderPanel = false;

  ngOnChanges(_: SimpleChanges): void {
    this.refreshClinicIdState();
  }

  public getClinicAddresses(clinicId: string | null): string[] {
    return this.clinics.find(clinic => clinic.name === clinicId)?.addresses ?? [];
  }

  public getFirstClinicProvidersAfterPending(): string[]{
    if(!this.firstClinicProps) return [];
    let result = this.getProvidersAfterPending(this.firstClinicProps);
    return result;
  }

  public getSecondClinicProvidersAfterPending(): string[]{
    if(!this.secondClinicProps) return [];
    let result = this.getProvidersAfterPending(
      this.secondClinicProps,
      this.shouldPreserveCurrentSecondClinicProviders()
    );
    return result;
  }

  public hasSecondClinicProvidersToDisplay(): boolean {
    if(!this.secondClinicProps) return false;

    return (this.secondClinicProps.providers?.length ?? 0) > 0
      || (this.secondClinicProps.pendingProviders?.length ?? 0) > 0
      || (this.shouldPreserveCurrentSecondClinicProviders()
        && (this.secondClinicProps.pendingRemovalProviders?.length ?? 0) > 0);
  }

  onRemoveSecondClinic(): void {
    this.secondClinicProps = null;
    [
      this.secondClinicCtrl,
      this.secondProvidersCtrl
    ].forEach((control) => {
      control.clearValidators();
      control.setValue(null);
      control.updateValueAndValidity({ emitEvent: false });
      control.markAsDirty();
      control.markAsTouched();
    });
  }

  public getFirstClinicRemovalProviders(): string[]{
    return this.firstClinicProps?.requestedRemovalProviders.map((provider) => provider.name) ?? [];
  }

  public getSecondClinicRemovalProviders(): string[]{
    return this.secondClinicProps?.requestedRemovalProviders.map((provider) => provider.name) ?? [];
  }

  public getFirstClinicRemovalProviderIds(): string[]{
    return this.firstClinicProps?.requestedRemovalProviders.map((provider) => provider.id) ?? [];
  }

  public getSecondClinicRemovalProviderIds(): string[]{
    return this.secondClinicProps?.requestedRemovalProviders.map((provider) => provider.id) ?? [];
  }

  onFirstClinicOtherProviderRemoved(): void {
    this.removeFirstClinicOtherProviderPanel = true;
    this.firstClinicOtherProviderRemoved.emit();
  };

  onSecondClinicOtherProviderRemoved(): void {
    this.removeSecondClinicOtherProviderPanel = true;
    this.secondClinicOtherProviderRemoved.emit();
  };

  onFirstClinicRemoveRequestRemoved(): void {
    this.removeFirstRemoveProviderPanel = true;
    this.firstClinicRemoveRequestRemoved.emit();
  };

  onSecondClinicRemoveRequestRemoved(): void {
    this.removeSecondRemoveProviderPanel = true;
    this.secondClinicRemoveRequestRemoved.emit();
  };

  private getProvidersAfterPending(
    props: ClinicProviderInfoSectionProps,
    preserveCurrentProviders = false
  ): string[] {
    const removeNames = preserveCurrentProviders
      ? []
      : props.pendingRemovalProviders?.map((provider) => provider.name) ?? [];

    const currentProviders = preserveCurrentProviders
      ? [...(props.providers ?? []), ...(props.pendingRemovalProviders ?? [])]
      : props.providers ?? [];

    const uniqueCurrentProviders = Array.from(
      new Map(currentProviders.map((provider) => [provider.id, provider])).values()
    );

    const providerNames = uniqueCurrentProviders
      .sort((a, b) => a.lastName.localeCompare(b.lastName) || a.firstName.localeCompare(b.firstName))
      .filter((provider) => !removeNames.includes(provider.name))
      .map((provider) => provider.name);

    const pendingNames = props.pendingProviders
      ?.sort((a, b) => a.lastName.localeCompare(b.lastName) || a.firstName.localeCompare(b.firstName))
      ?.filter((provider) =>!removeNames.includes(provider.name))
      ?.map((provider) => `${provider.name} (Pending Approval)`) ?? [];

    return providerNames.concat(pendingNames);
  }

  private shouldPreserveCurrentSecondClinicProviders(): boolean {
    return !!this.secondClinicProps?.pendingRemovalClinicId;
  }

  private parseClinicId(clinicId: unknown): string | null {
    if (clinicId === null || clinicId === undefined || clinicId === '') {
      return null;
    }

    const parsedId = String(clinicId).trim();
    return parsedId.length > 0 ? parsedId : null;
  }

  private refreshClinicIdState() {
    this.firstClinicIdValue = this.parseClinicId(this.firstClinicProps?.clinicId);
    this.secondClinicIdValue = this.parseClinicId(this.secondClinicProps?.clinicId);
    this.hasFirstClinicId = this.firstClinicIdValue !== null;
    this.hasSecondClinicId = this.secondClinicIdValue !== null;
  }

  get allFirstProviderIds(): string[] {
    if(!this.firstClinicProps) return [];
    const providerIds = this.firstClinicProps.providers?.map((provider) => provider.id) ?? [];
    const pendingProviderIds = this.firstClinicProps.pendingProviders?.map((provider) => provider.id) ?? [];
    return providerIds.concat(pendingProviderIds);
  }

  get allSecondProviderIds() {
    if(!this.secondClinicProps) return [];
    const providerIds = this.secondClinicProps.providers?.map((provider) => provider.id) ?? [];
    const pendingProviderIds = this.secondClinicProps.pendingProviders?.map((provider) => provider.id) ?? [];
    const pendingRemovalProviderIds = this.shouldPreserveCurrentSecondClinicProviders()
      ? this.secondClinicProps.pendingRemovalProviders?.map((provider) => provider.id) ?? []
      : [];
    return Array.from(new Set(providerIds.concat(pendingProviderIds, pendingRemovalProviderIds)));
  }

  get hasFirstOtherProvider(): boolean {
    if(!this.firstClinicProps) return false;
    return this.firstClinicProps.otherProviderName !== null && this.firstClinicProps.otherProviderNpi !== null;
  }

  get hasSecondOtherProvider(): boolean {
    if(!this.secondClinicProps) return false;
    return this.secondClinicProps.otherProviderName !== null && this.secondClinicProps.otherProviderNpi !== null;
  }
}
